import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import {
  isLikelyBot,
  getDeviceType,
  getBrowser,
  getOS,
  getScreenResolution,
} from '@/lib/botDetection';
import {
  parseTrafficSource,
  getEnhancedUTMParams,
  getDeviceInfo,
  getClickIds,
  getPartnerFromURL,
} from '@/lib/trafficSourceDetection';
import { isProductionDomain } from '@/lib/productionDomainCheck';

const VISITOR_ID_KEY = 'xpand_visitor_id';
const SESSION_ID_KEY = 'xpand_session_id';
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const LAST_PAGE_KEY = 'xpand_last_page';

function generateId(): string {
  return 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function getVisitorId(): string {
  let visitorId = localStorage.getItem(VISITOR_ID_KEY);
  if (!visitorId) {
    visitorId = generateId();
    localStorage.setItem(VISITOR_ID_KEY, visitorId);
  }
  return visitorId;
}

function getSessionId(): string {
  const stored = sessionStorage.getItem(SESSION_ID_KEY);
  if (stored) {
    const { id, lastActivity } = JSON.parse(stored);
    if (Date.now() - lastActivity < SESSION_TIMEOUT) {
      return id;
    }
  }
  
  const newId = generateId();
  sessionStorage.setItem(SESSION_ID_KEY, JSON.stringify({
    id: newId,
    lastActivity: Date.now(),
  }));
  return newId;
}

function updateSessionActivity(sessionId: string): void {
  sessionStorage.setItem(SESSION_ID_KEY, JSON.stringify({
    id: sessionId,
    lastActivity: Date.now(),
  }));
}

function getPreviousPage(): string | null {
  return sessionStorage.getItem(LAST_PAGE_KEY);
}

function setCurrentPage(path: string): void {
  sessionStorage.setItem(LAST_PAGE_KEY, path);
}

export function useAnalyticsTracking() {
  const location = useLocation();
  const pageStartTime = useRef<number>(Date.now());
  const maxScrollDepth = useRef<number>(0);
  const isBot = useRef<boolean>(false);
  const sessionCreated = useRef<boolean>(false);
  const firstInteractionTime = useRef<number | null>(null);
  const initialized = useRef<boolean>(false);

  // Check production domain dynamically each time
  const shouldTrack = useCallback((): boolean => {
    if (typeof window === 'undefined') return false;
    
    const botCheck = isLikelyBot();
    const prodCheck = isProductionDomain();
    
    // Cache bot check since it won't change
    isBot.current = botCheck;
    
    return !botCheck && prodCheck;
  }, []);

  const trackEvent = useCallback(async (
    eventType: string,
    additionalData: Record<string, unknown> = {}
  ) => {
    // Check dynamically each time
    if (!shouldTrack()) {
      return;
    }

    const visitorId = getVisitorId();
    const sessionId = getSessionId();
    updateSessionActivity(sessionId);

    // Enhanced UTM and traffic source tracking
    const utmParams = getEnhancedUTMParams();
    const deviceInfo = getDeviceInfo();
    const trafficSource = parseTrafficSource(document.referrer, window.location.search);
    const clickIds = getClickIds();
    const partnerId = getPartnerFromURL();
    const previousPage = getPreviousPage();

    // Get authenticated user if available
    let userId: string | null = null;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      userId = user?.id || null;
    } catch {
      // Ignore auth errors
    }

    try {
      const { error } = await supabase.from('platform_analytics').insert([{
        session_id: sessionId,
        visitor_id: visitorId,
        user_id: userId,
        page_path: location.pathname,
        page_title: document.title,
        event_type: eventType,
        event_data: additionalData as unknown as Record<string, never>,
        
        // Traffic source (enhanced)
        referrer: document.referrer || null,
        utm_source: utmParams.utm_source || trafficSource.source,
        utm_medium: utmParams.utm_medium || trafficSource.medium,
        utm_campaign: utmParams.utm_campaign,
        utm_term: utmParams.utm_term,
        utm_content: utmParams.utm_content,
        
        // Ad tracking
        ad_platform: trafficSource.adPlatform || null,
        gclid: clickIds.gclid || null,
        fbclid: clickIds.fbclid || null,
        msclkid: clickIds.msclkid || null,
        partner_id: partnerId,
        
        // Device info
        device_type: getDeviceType(),
        browser: getBrowser(),
        os: getOS(),
        screen_resolution: getScreenResolution(),
        language: deviceInfo.language,
        timezone: deviceInfo.timezone,
        viewport_size: deviceInfo.viewportSize,
        connection_type: deviceInfo.connectionType,
        
        // User state
        is_bot: false,
        is_new_visitor: deviceInfo.isNewVisitor,
        user_agent: navigator.userAgent,
        
        // Page interaction
        duration_ms: eventType === 'page_exit' ? Date.now() - pageStartTime.current : 0,
        scroll_depth: maxScrollDepth.current,
        previous_page: previousPage,
        interaction_time_ms: firstInteractionTime.current,
        
        // Click tracking
        click_target: (additionalData.target as string) || null,
        click_text: (additionalData.text as string) || null,
      }]);

      if (error) {
        console.error('Analytics insert error:', error);
      }
    } catch (error) {
      console.error('Analytics tracking error:', error);
    }
  }, [location.pathname, shouldTrack]);

  const createOrUpdateSession = useCallback(async () => {
    // Skip session creation for bots and non-production environments
    if (!shouldTrack()) return;

    const visitorId = getVisitorId();
    const sessionId = getSessionId();
    const utmParams = getEnhancedUTMParams();
    const trafficSource = parseTrafficSource(document.referrer, window.location.search);

    try {
      const { data: existingSession } = await supabase
        .from('analytics_sessions')
        .select('id, total_pageviews')
        .eq('session_id', sessionId)
        .maybeSingle();

      if (existingSession) {
        // Update existing session
        const { error } = await supabase
          .from('analytics_sessions')
          .update({
            ended_at: new Date().toISOString(),
            total_pageviews: (existingSession.total_pageviews || 0) + 1,
            exit_page: location.pathname,
            is_bounce: false,
          })
          .eq('session_id', sessionId);
          
        if (error) {
          console.error('Session update error:', error);
        }
      } else {
        // Create new session with enhanced tracking
        const { error } = await supabase.from('analytics_sessions').insert({
          session_id: sessionId,
          visitor_id: visitorId,
          entry_page: location.pathname,
          exit_page: location.pathname,
          device_type: getDeviceType(),
          browser: getBrowser(),
          os: getOS(),
          referrer: document.referrer || null,
          utm_source: utmParams.utm_source || trafficSource.source,
          utm_medium: utmParams.utm_medium || trafficSource.medium,
          utm_campaign: utmParams.utm_campaign,
          is_bot: false,
          total_pageviews: 1,
        });
        
        if (error) {
          console.error('Session insert error:', error);
        } else {
          sessionCreated.current = true;
        }
      }
    } catch (error) {
      console.error('Session tracking error:', error);
    }
  }, [location.pathname, shouldTrack]);

  // Track pageviews on route change
  useEffect(() => {
    const previousPage = getPreviousPage();
    pageStartTime.current = Date.now();
    maxScrollDepth.current = 0;
    firstInteractionTime.current = null;
    
    trackEvent('pageview', { previous_page: previousPage });
    createOrUpdateSession();
    
    // Store current page for next navigation
    setCurrentPage(location.pathname);
  }, [location.pathname, trackEvent, createOrUpdateSession]);

  // Track scroll depth
  useEffect(() => {
    if (isBot.current) return;

    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = docHeight > 0 ? Math.round((scrollTop / docHeight) * 100) : 0;
      maxScrollDepth.current = Math.max(maxScrollDepth.current, scrollPercent);
      
      // Track first interaction
      if (firstInteractionTime.current === null) {
        firstInteractionTime.current = Date.now() - pageStartTime.current;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Track clicks on interactive elements
  useEffect(() => {
    if (isBot.current) return;

    const handleClick = (e: MouseEvent) => {
      // Track first interaction
      if (firstInteractionTime.current === null) {
        firstInteractionTime.current = Date.now() - pageStartTime.current;
      }
      
      const target = e.target as HTMLElement;
      const clickable = target.closest('button, a, [role="button"], input[type="submit"]');
      
      if (clickable) {
        const tagName = clickable.tagName.toLowerCase();
        const text = clickable.textContent?.trim().slice(0, 100) || '';
        const href = (clickable as HTMLAnchorElement).href || '';
        const id = clickable.id || '';
        const className = clickable.className || '';
        const dataAction = clickable.getAttribute('data-action') || '';

        trackEvent('click', {
          target: `${tagName}${id ? '#' + id : ''}`,
          text,
          href,
          className: typeof className === 'string' ? className.slice(0, 200) : '',
          data_action: dataAction,
        });
      }
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [trackEvent]);

  // Track form interactions
  useEffect(() => {
    if (isBot.current) return;

    const handleFormSubmit = (e: SubmitEvent) => {
      const form = e.target as HTMLFormElement;
      trackEvent('form_submit', {
        form_id: form.id || 'unknown',
        form_action: form.action || '',
        form_name: form.name || '',
      });
    };

    const handleFormFocus = (e: FocusEvent) => {
      const target = e.target as HTMLInputElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
        if (firstInteractionTime.current === null) {
          firstInteractionTime.current = Date.now() - pageStartTime.current;
        }
        trackEvent('form_focus', {
          field_name: target.name || target.id || 'unknown',
          field_type: target.type || target.tagName.toLowerCase(),
        });
      }
    };

    document.addEventListener('submit', handleFormSubmit);
    document.addEventListener('focusin', handleFormFocus);
    
    return () => {
      document.removeEventListener('submit', handleFormSubmit);
      document.removeEventListener('focusin', handleFormFocus);
    };
  }, [trackEvent]);

  // Track page exit / time on page
  useEffect(() => {
    if (isBot.current) return;

    const handleBeforeUnload = () => {
      const sessionId = getSessionId();
      const deviceInfo = getDeviceInfo();
      
      // Use sendBeacon for reliable exit tracking
      const data = JSON.stringify({
        session_id: sessionId,
        visitor_id: getVisitorId(),
        page_path: location.pathname,
        event_type: 'page_exit',
        duration_ms: Date.now() - pageStartTime.current,
        scroll_depth: maxScrollDepth.current,
        interaction_time_ms: firstInteractionTime.current,
        language: deviceInfo.language,
        timezone: deviceInfo.timezone,
      });

      navigator.sendBeacon?.(
        `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/platform_analytics`,
        new Blob([data], { type: 'application/json' })
      );
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [location.pathname]);

  // Track visibility changes (tab switching)
  useEffect(() => {
    if (isBot.current) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        trackEvent('tab_hidden', {
          time_on_page: Date.now() - pageStartTime.current,
        });
      } else {
        trackEvent('tab_visible', {});
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [trackEvent]);

  return { trackEvent };
}
